import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CreditPack } from '@claude-code-api/shared';

export default function AdminCreditPlansPage() {
  const { t } = useTranslation(['adminCreditPlans', 'common']);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPack, setEditingPack] = useState<CreditPack | null>(null);
  const [saving, setSaving] = useState(false);

  const [formName, setFormName] = useState('');
  const [formCredits, setFormCredits] = useState(1000);
  const [formPrice, setFormPrice] = useState(12);
  const [formOrder, setFormOrder] = useState(0);

  const fetchPacks = async () => {
    try {
      const res = await fetch('/api/admin/credit-packs', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPacks(data.data || []);
      }
    } catch {
      toast.error(t('adminCreditPlans:errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPacks();
  }, []);

  const openCreate = () => {
    setEditingPack(null);
    setFormName('');
    setFormCredits(1000);
    setFormPrice(12);
    setFormOrder(0);
    setShowModal(true);
  };

  const openEdit = (pack: CreditPack) => {
    setEditingPack(pack);
    setFormName(pack.name);
    setFormCredits(Number(pack.creditAmount));
    setFormPrice(Number(pack.priceUsd));
    setFormOrder(pack.displayOrder);
    setShowModal(true);
  };

  const savePack = async () => {
    setSaving(true);
    try {
      const body = {
        name: formName,
        creditAmount: formCredits,
        priceUsd: formPrice,
        displayOrder: formOrder,
      };
      const url = editingPack
        ? `/api/admin/credit-packs/${editingPack.id}`
        : '/api/admin/credit-packs';
      const method = editingPack ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(editingPack ? t('adminCreditPlans:messages.updated') : t('adminCreditPlans:messages.created'));
        setShowModal(false);
        void fetchPacks();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminCreditPlans:errors.save'));
      }
    } catch {
      toast.error(t('adminCreditPlans:errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pack: CreditPack) => {
    try {
      const res = await fetch(`/api/admin/credit-packs/${pack.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !pack.isActive }),
      });
      if (res.ok) {
        toast.success(pack.isActive ? t('adminCreditPlans:messages.deactivated') : t('adminCreditPlans:messages.activated'));
        void fetchPacks();
      }
    } catch {
      toast.error(t('adminCreditPlans:errors.update'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('adminCreditPlans:title')} description={t('adminCreditPlans:descriptionShort')} />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title={t('adminCreditPlans:title')} description={t('adminCreditPlans:description')} />
        <Button onClick={openCreate}>{t('adminCreditPlans:actions.createPlan')}</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('adminCreditPlans:table.name')}</TableHead>
              <TableHead>{t('adminCreditPlans:table.credits')}</TableHead>
              <TableHead>{t('adminCreditPlans:table.priceUsd')}</TableHead>
              <TableHead>{t('adminCreditPlans:table.displayOrder')}</TableHead>
              <TableHead>{t('adminCreditPlans:table.status')}</TableHead>
              <TableHead>{t('adminCreditPlans:table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {packs.map((pack) => (
              <TableRow key={pack.id}>
                <TableCell className="font-medium">{pack.name}</TableCell>
                <TableCell>{Number(pack.creditAmount).toLocaleString()}</TableCell>
                <TableCell>${Number(pack.priceUsd).toFixed(2)}</TableCell>
                <TableCell>{pack.displayOrder}</TableCell>
                <TableCell>
                  <Badge variant={pack.isActive ? 'default' : 'secondary'}>
                    {pack.isActive ? t('adminCreditPlans:status.active') : t('adminCreditPlans:status.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(pack)}>{t('adminCreditPlans:actions.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(pack)}>
                      {pack.isActive ? t('adminCreditPlans:actions.deactivate') : t('adminCreditPlans:actions.activate')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPack ? t('adminCreditPlans:dialog.editTitle') : t('adminCreditPlans:dialog.createTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('adminCreditPlans:form.name')}</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={t('adminCreditPlans:form.namePlaceholder')} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>{t('adminCreditPlans:form.credits')}</Label>
                <Input type="number" min={0} value={formCredits} onChange={(e) => setFormCredits(Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>{t('adminCreditPlans:form.priceUsd')}</Label>
                <Input type="number" min={0} step={0.01} value={formPrice} onChange={(e) => setFormPrice(Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>{t('adminCreditPlans:form.displayOrder')}</Label>
                <Input type="number" min={0} value={formOrder} onChange={(e) => setFormOrder(Number(e.target.value))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>{t('common:cancel')}</Button>
            <Button onClick={savePack} disabled={saving || !formName}>
              {saving ? t('adminCreditPlans:actions.saving') : editingPack ? t('adminCreditPlans:actions.update') : t('adminCreditPlans:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
